import {
  createMonitorInput,
  MONITOR_DEFAULTS,
  MONITOR_METHODS,
  MONITOR_MODES,
  type MonitorDto,
  type MonitorMethod,
  type MonitorMode,
  updateMonitorInput,
} from '@fos/shared/contracts/monitoring';
import {
  MONITOR_METHOD_LABELS,
  MONITOR_MODE_DESCRIPTIONS,
  MONITOR_MODE_LABELS,
  monitorIntervalLabel,
} from '@fos/shared/labels';
import { Archive } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { Select, type SelectOption } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { INTERVAL_OPTIONS, THRESHOLD_OPTIONS, TIMEOUT_OPTIONS } from '../monitoring-format';

const FIELD_KEYS = [
  'url',
  'name',
  'method',
  'expectedStatus',
  'keyword',
  'intervalSec',
  'timeoutMs',
  'failThreshold',
  'recoverThreshold',
  'mode',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

interface FormState {
  url: string;
  name: string;
  method: MonitorMethod;
  expectedStatus: string;
  keyword: string;
  intervalSec: string;
  timeoutMs: string;
  failThreshold: string;
  recoverThreshold: string;
  mode: MonitorMode;
  enabled: boolean;
}

function initial(
  monitor: MonitorDto | null,
  defaults: { url: string | null; name: string; mode: MonitorMode },
): FormState {
  return {
    url: monitor?.url ?? defaults.url ?? 'https://',
    name: monitor?.name ?? defaults.name,
    method: monitor?.method ?? MONITOR_DEFAULTS.method,
    expectedStatus: String(monitor?.expectedStatus ?? MONITOR_DEFAULTS.expectedStatus),
    keyword: monitor?.keyword ?? '',
    intervalSec: String(monitor?.intervalSec ?? MONITOR_DEFAULTS.intervalSec),
    timeoutMs: String(monitor?.timeoutMs ?? MONITOR_DEFAULTS.timeoutMs),
    failThreshold: String(monitor?.failThreshold ?? MONITOR_DEFAULTS.failThreshold),
    recoverThreshold: String(monitor?.recoverThreshold ?? MONITOR_DEFAULTS.recoverThreshold),
    mode: monitor?.mode ?? defaults.mode,
    enabled: monitor?.enabled ?? true,
  };
}

function issuesToErrors(issues: Array<{ path: string; message: string }>): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const issue of issues) {
    const key = FIELD_KEYS.find((k) => k === issue.path.split('.')[0]);
    if (key && !out[key]) out[key] = issue.message;
  }
  return out;
}

const intervalOptions: SelectOption[] = INTERVAL_OPTIONS.map((s) => ({
  value: String(s),
  label: `${monitorIntervalLabel(s)}${s === MONITOR_DEFAULTS.intervalSec ? ' (domyślnie)' : ''}`,
}));
const timeoutOptions: SelectOption[] = TIMEOUT_OPTIONS.map((ms) => ({ value: String(ms), label: `${ms / 1000} s` }));
const failOptions: SelectOption[] = THRESHOLD_OPTIONS.map((n) => ({
  value: String(n),
  label: n === 1 ? 'po 1 błędzie' : `po ${n} błędach z rzędu`,
}));
const recoverOptions: SelectOption[] = THRESHOLD_OPTIONS.map((n) => ({
  value: String(n),
  label: n === 1 ? 'po 1 udanym sprawdzeniu' : `po ${n} udanych z rzędu`,
}));
const methodOptions: SelectOption<MonitorMethod>[] = MONITOR_METHODS.map((m) => ({
  value: m,
  label: MONITOR_METHOD_LABELS[m],
}));

/**
 * Dodanie i edycja monitora zasobu (widok bez osobnego ekranu w Figmie — wzorzec modalu „Dodaj zasób”): adres,
 * interwał (domyślnie co 2 min), progi alarmu i poprawy (domyślnie 2 / 2), oczekiwany status, słowo kluczowe, metoda
 * i limit czasu. Tryb (symulacja / prawdziwe sprawdzanie) — tylko owner/admin. Edycja: włączenie i „Wyłącz z użycia”.
 */
export function MonitorDialog({
  open,
  onOpenChange,
  assetId,
  assetName,
  monitor,
  onSaved,
  suggestedUrl,
  defaultMode,
  canManageMode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  assetName: string;
  /** null = nowy monitor */
  monitor: MonitorDto | null;
  /** po zapisie, przed zamknięciem okna (włączenie i wyłączenie z użycia zmieniają przycisk, który otworzył okno) */
  onSaved?: (kind: 'created' | 'updated' | 'archived') => void;
  suggestedUrl: string | null;
  defaultMode: MonitorMode;
  canManageMode: boolean;
}) {
  const utils = trpc.useUtils();
  const create = trpc.monitoring.createMonitor.useMutation();
  const update = trpc.monitoring.updateMonitor.useMutation();
  const archive = trpc.monitoring.archiveMonitor.useMutation();
  const defaults = { url: suggestedUrl, name: assetName, mode: defaultMode };
  const [form, setForm] = useState<FormState>(() => initial(monitor, defaults));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(initial(monitor, defaults));
    setErrors({});
    setFormError(null);
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const done = async (message: string) => {
    toast.success(message);
    await Promise.all([utils.monitoring.invalidate(), utils.maintenance.invalidate()]);
    onOpenChange(false);
  };

  const fail = (err: unknown) => {
    if (appErrorCode(err) === 'VALIDATION_ERROR') {
      const fieldErrors = issuesToErrors(validationIssues(err));
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
    }
    setFormError(errorMessage(err));
  };

  const values = () => ({
    url: form.url.trim(),
    name: form.name.trim(),
    method: form.method,
    expectedStatus: Number(form.expectedStatus),
    keyword: form.keyword.trim() ? form.keyword.trim() : null,
    intervalSec: Number(form.intervalSec),
    timeoutMs: Number(form.timeoutMs),
    failThreshold: Number(form.failThreshold),
    recoverThreshold: Number(form.recoverThreshold),
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setErrors({});
    if (!monitor) {
      const input = {
        assetId,
        ...values(),
        ...(canManageMode && form.mode !== defaultMode ? { mode: form.mode } : {}),
      };
      const parsed = createMonitorInput.safeParse(input);
      if (!parsed.success) {
        setErrors(issuesToErrors(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }))));
        return;
      }
      try {
        await create.mutateAsync(input);
        onSaved?.('created');
        await done('Monitoring włączony');
      } catch (err) {
        fail(err);
      }
      return;
    }
    const input = {
      id: monitor.id,
      configHash: monitor.configHash,
      ...values(),
      enabled: form.enabled,
      ...(canManageMode && form.mode !== monitor.mode ? { mode: form.mode } : {}),
    };
    const parsed = updateMonitorInput.safeParse(input);
    if (!parsed.success) {
      setErrors(issuesToErrors(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }))));
      return;
    }
    try {
      await update.mutateAsync(input);
      onSaved?.('updated');
      await done('Zapisano monitor');
    } catch (err) {
      fail(err);
    }
  };

  const retire = async () => {
    if (!monitor) return;
    setFormError(null);
    try {
      await archive.mutateAsync({ id: monitor.id });
      onSaved?.('archived');
      await done('Monitoring wyłączony');
    } catch (err) {
      fail(err);
    }
  };

  const pending = create.isPending || update.isPending || archive.isPending;
  const formId = 'monitor-form';

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={monitor ? 'Monitoring strony' : 'Włącz monitoring'}
      className={FULLSCREEN_ON_PHONE}
      description={
        monitor ? undefined : 'Sprawdzamy stronę co kilka minut. Pojedynczy błąd nie alarmuje — alarm dopiero po serii.'
      }
      footer={
        <>
          {monitor && (
            <Button variant="ghost" icon={Archive} onClick={retire} disabled={pending} className="mr-auto">
              Wyłącz z użycia
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form={formId} loading={create.isPending || update.isPending}>
            {monitor ? 'Zapisz' : 'Włącz monitoring'}
          </Button>
        </>
      }
    >
      <form id={formId} ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Adres strony" error={errors.url} hint="Tylko publiczne adresy, porty 80 i 443.">
          <Input
            value={form.url}
            onChange={(e) => set('url', e.target.value)}
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Sprawdzaj" error={errors.intervalSec}>
            <Select value={form.intervalSec} onValueChange={(v) => set('intervalSec', v)} options={intervalOptions} />
          </Field>
          <Field label="Limit czasu odpowiedzi" error={errors.timeoutMs}>
            <Select value={form.timeoutMs} onValueChange={(v) => set('timeoutMs', v)} options={timeoutOptions} />
          </Field>
          <Field label="Alarm" error={errors.failThreshold}>
            <Select value={form.failThreshold} onValueChange={(v) => set('failThreshold', v)} options={failOptions} />
          </Field>
          <Field label="Koniec awarii" error={errors.recoverThreshold}>
            <Select
              value={form.recoverThreshold}
              onValueChange={(v) => set('recoverThreshold', v)}
              options={recoverOptions}
            />
          </Field>
          <Field label="Oczekiwany status HTTP" error={errors.expectedStatus}>
            <Input
              value={form.expectedStatus}
              onChange={(e) => set('expectedStatus', e.target.value.replace(/\D/g, '').slice(0, 3))}
              inputMode="numeric"
              mono
            />
          </Field>
          <Field label="Metoda" error={errors.method}>
            <Select<MonitorMethod>
              value={form.method}
              onValueChange={(v) => set('method', v)}
              options={methodOptions}
            />
          </Field>
        </div>
        <Field
          label="Tekst na stronie (opcjonalnie)"
          error={errors.keyword}
          hint="Np. „Kontakt” — brak tego tekstu też jest błędem. Wymaga metody GET."
        >
          <Input value={form.keyword} onChange={(e) => set('keyword', e.target.value)} autoComplete="off" />
        </Field>
        {canManageMode && (
          <Field label="Tryb sprawdzania" error={errors.mode} hint={MONITOR_MODE_DESCRIPTIONS[form.mode]}>
            <Segmented<MonitorMode>
              aria-label="Tryb sprawdzania"
              value={form.mode}
              onValueChange={(v) => set('mode', v)}
              options={MONITOR_MODES.map((m) => ({ value: m, label: MONITOR_MODE_LABELS[m] }))}
            />
          </Field>
        )}
        {!canManageMode && (
          <p className="text-caption text-fg-muted">
            Tryb: {MONITOR_MODE_LABELS[form.mode].toLowerCase()}. Zmienia go właściciel albo administrator.
          </p>
        )}
        {monitor && (
          <Checkbox checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} label="Sprawdzanie włączone" />
        )}
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
